// src/settings/StaffSettings.js - 職種選択対応版
import React, { useState, useEffect } from "react";
import "./StaffSettings.css";
import { getStaffList, addStaff, deleteStaff, updateStaff } from "../supabase";
import { supabase } from "../supabase";
import { useOrganization } from "../contexts/OrganizationContext";
import StaffCsvImportModal from "./StaffCsvImportModal";

function StaffSettings() {
  const { currentOrganization } = useOrganization();

  const [staff, setStaff] = useState({
    id: "",
    lastName: "",
    firstName: "",
    email: "",
    year: "",
    role: "staff",
    jobTypeId: "",
  });
  const [staffList, setStaffList] = useState([]);
  const [retiredList, setRetiredList] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [filter, setFilter] = useState("");
  const [retiredFilter, setRetiredFilter] = useState("");
  const [editingStaff, setEditingStaff] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRetired, setShowRetired] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  useEffect(() => {
    if (currentOrganization?.id) {
      fetchData();
    }
  }, [currentOrganization?.id]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const organizationId = currentOrganization?.id || 'default-hospital';

      // 職種マスター取得
      const { data: jobTypesData, error: jobTypesError } = await supabase
        .from('job_types')
        .select('*')
        .eq('organization_id', organizationId)
        .order('display_order', { ascending: true });

      if (jobTypesError) throw jobTypesError;
      setJobTypes(jobTypesData || []);

      // 在籍中スタッフ（is_active = true）
      const { data: activeData, error: activeError } = await supabase
        .from('staff')
        .select('*, job_types(id, name)')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('year', { ascending: true });

      if (activeError) throw activeError;
      setStaffList(activeData || []);

      // 退職者（is_active = false）
      const { data: retiredData, error: retiredError } = await supabase
        .from('staff')
        .select('*, job_types(id, name)')
        .eq('organization_id', organizationId)
        .eq('is_active', false)
        .order('last_name', { ascending: true });

      if (retiredError) throw retiredError;
      setRetiredList(retiredData || []);

      console.log(`✅ 在籍: ${(activeData || []).length}人, 退職者: ${(retiredData || []).length}人, 職種: ${(jobTypesData || []).length}件`);
    } catch (err) {
      console.error("スタッフ一覧取得エラー:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      if (!staff.id || !staff.lastName || !staff.firstName || !staff.email) {
        showMessage("必須項目を入力してください", 'error');
        return;
      }
      if (!currentOrganization?.id) {
        showMessage("組織情報が取得できません", 'error');
        return;
      }
      const exists = staffList.some((s) => s.employee_id === staff.id);
      if (exists) {
        showMessage("その社員番号はすでに登録されています", 'error');
        return;
      }

      setIsLoading(true);

      // addStaff経由でauth登録
      await addStaff(
        staff.id,
        staff.lastName,
        staff.firstName,
        staff.email,
        staff.year,
        staff.role,
        currentOrganization.id
      );

      // job_type_idを別途更新
      if (staff.jobTypeId) {
        const { data: newStaff } = await supabase
          .from('staff')
          .select('id')
          .eq('organization_id', currentOrganization.id)
          .eq('employee_id', staff.id)
          .single();

        if (newStaff) {
          await supabase
            .from('staff')
            .update({ job_type_id: staff.jobTypeId })
            .eq('id', newStaff.id);
        }
      }

      showMessage("スタッフを追加しました", 'success');
      setStaff({ id: "", lastName: "", firstName: "", email: "", year: "", role: "staff", jobTypeId: "" });
      await fetchData();
    } catch (err) {
      console.error("スタッフ追加エラー:", err);
      showMessage("追加に失敗しました: " + err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm("本当に削除しますか？この操作は取り消せません。")) return;
    try {
      setIsLoading(true);
      await deleteStaff(staffId);
      showMessage("スタッフを削除しました", 'success');
      await fetchData();
    } catch (err) {
      console.error("スタッフ削除エラー:", err);
      showMessage("削除に失敗しました: " + err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetire = async (s) => {
    if (!window.confirm(`${s.last_name} ${s.first_name} さんを退職者として登録しますか？`)) return;
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('staff')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', s.id);
      if (error) throw error;
      showMessage(`${s.last_name} ${s.first_name} さんを退職者に登録しました`, 'success');
      await fetchData();
    } catch (err) {
      console.error("退職処理エラー:", err);
      showMessage("退職処理に失敗しました: " + err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReinstate = async (s) => {
    if (!window.confirm(`${s.last_name} ${s.first_name} さんを復職させますか？`)) return;
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('staff')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('id', s.id);
      if (error) throw error;
      showMessage(`${s.last_name} ${s.first_name} さんを復職させました`, 'success');
      await fetchData();
    } catch (err) {
      console.error("復職処理エラー:", err);
      showMessage("復職処理に失敗しました: " + err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (s) => {
    setEditingStaff({
      id: s.id,
      employeeId: s.employee_id,
      lastName: s.last_name,
      firstName: s.first_name,
      email: s.email,
      year: s.year,
      role: s.role,
      jobTypeId: s.job_type_id || "",
    });
    setEditModalOpen(true);
  };

  const saveEdit = async () => {
    try {
      if (!editingStaff.employeeId || !editingStaff.lastName || !editingStaff.firstName || !editingStaff.email) {
        showMessage("必須項目を入力してください", 'error');
        return;
      }
      setIsLoading(true);

      await updateStaff(
        editingStaff.id,
        editingStaff.employeeId,
        editingStaff.lastName,
        editingStaff.firstName,
        editingStaff.email,
        editingStaff.year,
        editingStaff.role
      );

      // 職種更新
      const { error: jobTypeError } = await supabase
        .from('staff')
        .update({
          job_type_id: editingStaff.jobTypeId || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingStaff.id);

      if (jobTypeError) throw jobTypeError;

      showMessage("スタッフ情報を更新しました", 'success');
      setEditModalOpen(false);
      await fetchData();
    } catch (err) {
      console.error("スタッフ更新エラー:", err);
      showMessage("更新に失敗しました: " + err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    const message = document.createElement("div");
    message.textContent = text;
    message.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background-color: ${type === 'success' ? '#4caf50' : '#f44336'};
      color: white;
      padding: 10px 20px;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      z-index: 1000;
    `;
    document.body.appendChild(message);
    setTimeout(() => {
      if (document.body.contains(message)) document.body.removeChild(message);
    }, 3000);
  };

  const filteredList = staffList.filter((s) =>
    [s.employee_id, s.last_name, s.first_name].some((field) =>
      (field || "").toLowerCase().includes(filter.toLowerCase())
    )
  );

  const filteredRetiredList = retiredList.filter((s) =>
    [s.employee_id, s.last_name, s.first_name].some((field) =>
      (field || "").toLowerCase().includes(retiredFilter.toLowerCase())
    )
  );

  const getJobTypeName = (s) => s.job_types?.name || '未設定';

  return (
    <div className="settings-container">
      <div style={{ backgroundColor: '#e7f3ff', padding: '1rem', marginBottom: '1rem', borderRadius: '4px' }}>
        <h3 style={{ margin: 0, color: '#0066cc' }}>📋 スタッフ管理 (Supabase版)</h3>
        <small style={{ color: '#666' }}>
          在籍中: {staffList.length}人　退職者: {retiredList.length}人
        </small>
      </div>

      <h2 className="settings-title">スタッフ登録・管理</h2>

      {error && (
        <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          エラー: {error}
        </div>
      )}

      {/* 追加フォーム */}
      <table className="staff-form-table">
        <tbody>
          <tr>
            <td>社員番号</td>
            <td><input type="text" value={staff.id} onChange={(e) => setStaff({ ...staff, id: e.target.value })} /></td>
          </tr>
          <tr>
            <td>姓</td>
            <td><input type="text" value={staff.lastName} onChange={(e) => setStaff({ ...staff, lastName: e.target.value })} /></td>
          </tr>
          <tr>
            <td>名</td>
            <td><input type="text" value={staff.firstName} onChange={(e) => setStaff({ ...staff, firstName: e.target.value })} /></td>
          </tr>
          <tr>
            <td>メールアドレス</td>
            <td><input type="email" value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} /></td>
          </tr>
          <tr>
            <td>入職年</td>
            <td><input type="text" value={staff.year} onChange={(e) => setStaff({ ...staff, year: e.target.value })} /></td>
          </tr>
          <tr>
            <td>権限</td>
            <td>
              <select value={staff.role} onChange={(e) => setStaff({ ...staff, role: e.target.value })}>
                <option value="admin">管理者</option>
                <option value="staff">スタッフ</option>
              </select>
            </td>
          </tr>
          <tr>
            <td>職種</td>
            <td>
              <select value={staff.jobTypeId} onChange={(e) => setStaff({ ...staff, jobTypeId: e.target.value })}>
                <option value="">未設定</option>
                {jobTypes.map((jt) => (
                  <option key={jt.id} value={jt.id}>{jt.name}</option>
                ))}
              </select>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button className="add-btn" onClick={handleAdd} disabled={isLoading}>
          {isLoading ? '追加中...' : 'スタッフを追加'}
        </button>
        <button
          type="button"
          onClick={() => setCsvModalOpen(true)}
          disabled={isLoading}
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            fontWeight: 600,
            opacity: isLoading ? 0.5 : 1,
          }}
        >
          📥 CSV 一括登録
        </button>
      </div>

      {/* 在籍中スタッフ一覧 */}
      <div className="filter-input">
        <input
          type="text"
          placeholder="社員番号または氏名で検索"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {isLoading && staffList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>読み込み中...</div>
      ) : (
        <div className="staff-list-scroll">
          <table className="staff-list-table">
            <thead>
              <tr>
                <th>社員番号</th>
                <th>氏名</th>
                <th>職種</th>
                <th>メール</th>
                <th>入職年</th>
                <th>権限</th>
                <th>変更</th>
                <th>退職</th>
                <th>削除</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((s) => (
                <tr key={s.id}>
                  <td>{s.employee_id}</td>
                  <td>{s.last_name} {s.first_name}</td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: '12px', fontSize: '12px',
                      backgroundColor: s.job_types ? '#e3f2fd' : '#f5f5f5',
                      color: s.job_types ? '#1565c0' : '#999', fontWeight: '500',
                    }}>
                      {getJobTypeName(s)}
                    </span>
                  </td>
                  <td>{s.email}</td>
                  <td>{s.year}</td>
                  <td>{s.role}</td>
                  <td>
                    <button className="edit-btn" onClick={() => handleEdit(s)} disabled={isLoading}>変更</button>
                  </td>
                  <td>
                    <button
                      onClick={() => handleRetire(s)}
                      disabled={isLoading}
                      style={{ padding: '4px 10px', backgroundColor: '#ff9800', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      退職
                    </button>
                  </td>
                  <td>
                    <button className="delete-btn" onClick={() => handleDelete(s.id)} disabled={isLoading}>削除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 退職者リスト（折りたたみ） */}
      <div style={{ marginTop: '2rem' }}>
        <button
          onClick={() => setShowRetired(v => !v)}
          style={{
            width: '100%', padding: '0.75rem 1rem',
            backgroundColor: showRetired ? '#6c757d' : '#f8f9fa',
            color: showRetired ? 'white' : '#495057',
            border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer',
            textAlign: 'left', fontWeight: '600', fontSize: '0.95rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          <span>🗂️ 退職者リスト（{retiredList.length}人）</span>
          <span>{showRetired ? '▲ 閉じる' : '▼ 開く'}</span>
        </button>

        {showRetired && (
          <div style={{ border: '1px solid #dee2e6', borderTop: 'none', borderRadius: '0 0 4px 4px', padding: '1rem', backgroundColor: '#f8f9fa' }}>
            <div className="filter-input" style={{ marginBottom: '0.75rem' }}>
              <input
                type="text"
                placeholder="社員番号または氏名で検索"
                value={retiredFilter}
                onChange={(e) => setRetiredFilter(e.target.value)}
              />
            </div>
            {filteredRetiredList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1rem', color: '#6c757d' }}>退職者はいません</div>
            ) : (
              <div className="staff-list-scroll">
                <table className="staff-list-table">
                  <thead>
                    <tr>
                      <th>社員番号</th>
                      <th>氏名</th>
                      <th>職種</th>
                      <th>メール</th>
                      <th>入職年</th>
                      <th>復職</th>
                      <th>削除</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRetiredList.map((s) => (
                      <tr key={s.id} style={{ backgroundColor: '#fff3cd' }}>
                        <td>{s.employee_id}</td>
                        <td>{s.last_name} {s.first_name}</td>
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: '12px', fontSize: '12px',
                            backgroundColor: s.job_types ? '#e3f2fd' : '#f5f5f5',
                            color: s.job_types ? '#1565c0' : '#999', fontWeight: '500',
                          }}>
                            {getJobTypeName(s)}
                          </span>
                        </td>
                        <td>{s.email}</td>
                        <td>{s.year}</td>
                        <td>
                          <button
                            onClick={() => handleReinstate(s)}
                            disabled={isLoading}
                            style={{ padding: '4px 10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            復職
                          </button>
                        </td>
                        <td>
                          <button className="delete-btn" onClick={() => handleDelete(s.id)} disabled={isLoading}>削除</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSV 一括登録モーダル */}
      <StaffCsvImportModal
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        onSuccess={() => { fetchData(); }}
        organizationId={currentOrganization?.id}
        existingEmployeeIds={staffList.map((s) => s.employee_id).filter(Boolean)}
        jobTypes={jobTypes}
      />

      {/* 編集モーダル */}
      {editModalOpen && editingStaff && (
        <div className="modal-backdrop" onClick={() => setEditModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>スタッフ情報の変更</h3>
              <button className="close-btn" onClick={() => setEditModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: "1rem" }}>
                <label>社員番号</label>
                <input type="text" value={editingStaff.employeeId} onChange={(e) => setEditingStaff({ ...editingStaff, employeeId: e.target.value })} style={{ width: "100%", padding: "0.5rem", border: "1px solid #ccc" }} />
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <label>姓</label>
                <input type="text" value={editingStaff.lastName} onChange={(e) => setEditingStaff({ ...editingStaff, lastName: e.target.value })} style={{ width: "100%", padding: "0.5rem", border: "1px solid #ccc" }} />
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <label>名</label>
                <input type="text" value={editingStaff.firstName} onChange={(e) => setEditingStaff({ ...editingStaff, firstName: e.target.value })} style={{ width: "100%", padding: "0.5rem", border: "1px solid #ccc" }} />
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <label>メール</label>
                <input type="email" value={editingStaff.email} onChange={(e) => setEditingStaff({ ...editingStaff, email: e.target.value })} style={{ width: "100%", padding: "0.5rem", border: "1px solid #ccc" }} />
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <label>入職年</label>
                <input type="text" value={editingStaff.year} onChange={(e) => setEditingStaff({ ...editingStaff, year: e.target.value })} style={{ width: "100%", padding: "0.5rem", border: "1px solid #ccc" }} />
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <label>権限</label>
                <select value={editingStaff.role} onChange={(e) => setEditingStaff({ ...editingStaff, role: e.target.value })} style={{ width: "100%", padding: "0.5rem", border: "1px solid #ccc" }}>
                  <option value="admin">管理者</option>
                  <option value="staff">スタッフ</option>
                </select>
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <label>職種</label>
                <select value={editingStaff.jobTypeId} onChange={(e) => setEditingStaff({ ...editingStaff, jobTypeId: e.target.value })} style={{ width: "100%", padding: "0.5rem", border: "1px solid #ccc" }}>
                  <option value="">未設定</option>
                  {jobTypes.map((jt) => (
                    <option key={jt.id} value={jt.id}>{jt.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginTop: "1.5rem", textAlign: "center" }}>
                <button onClick={saveEdit} disabled={isLoading} style={{ padding: "0.5rem 1rem", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                  {isLoading ? '保存中...' : '確定'}
                </button>
                <button onClick={() => setEditModalOpen(false)} disabled={isLoading} style={{ marginLeft: "1rem", padding: "0.5rem 1rem", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffSettings;